import { Chip, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { FiCreditCard, FiEdit, FiTrash2, FiZap } from "react-icons/fi";
import { SubscriptionConfigInterface } from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import { truncateText } from "../../common/utils/formatters";
import ConfirmDialog from "../../components/ConfirmDialog";
import CreateButton from "../../components/CreateButton";
import CustomTooltip from "../../components/CustomTooltip";
import {
  TypeSubscriptionStatusColor,
  TypeSubscriptionStatusLabel
} from "../../components/enum/typeSubscriptionStatusEnum";
import SubscriptionCreateModal from "../../components/modal/SubscriptionCreateModal";
import PaginationControls from "../../components/PaginationControls";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import { TextFeldUserSearch } from "../users/Users.styles";
import {
  ActionButton,
  Container,
  ProductSearchCard,
  Table,
  TableContainer,
  Title
} from "./subscription_config.styled";

export const SubscriptionConfig = () => {
  const [configs, setConfigs] = useState<SubscriptionConfigInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Controle do modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<SubscriptionConfigInterface | null>(null);

  // Controle do delete
  const [configToDelete, setConfigToDelete] =
    useState<SubscriptionConfigInterface | null>(null);

  // Controle do "gerar mensalidade agora"
  const [generatingId, setGeneratingId] = useState<number | null>(null);

  const { isAdmin } = useAuth();

  const containerId = "toaster-subscription-config";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const fetchConfigs = async (currentPage: number) => {
    try {
      const response = await api.get("/subscription/config", {
        params: {
          page: currentPage,
          limit: 8,
          search: search.trim() !== "" ? search.trim() : undefined,
          initial_date: startDate || undefined,
          final_date: endDate || undefined
        }
      });

      setConfigs(response.data.items);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao buscar configurações:", error);
      handleHttpError(error);
    }
  };

  useEffect(() => {
    fetchConfigs(page);
  }, [page, search, startDate, endDate]);

  // Ações do modal
  const handleOpenCreate = () => {
    setSelectedSubscription(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (config: SubscriptionConfigInterface) => {
    setSelectedSubscription(config);
    setModalOpen(true);
  };

  const handleSubscriptionSaved = () => {
    fetchConfigs(page);
  };

  const handleConfirmDelete = async () => {
    if (!configToDelete) return;
    try {
      await api.delete(`/subscription/config/${configToDelete.config_id}`);
      autoSuccess("Assinatura excluída com sucesso!");
      fetchConfigs(page);
    } catch (error: any) {
      console.error("Erro ao excluir assinatura:", error);
      handleHttpError(error);
    } finally {
      setConfigToDelete(null);
    }
  };

  // Dispara a geração imediata da mensalidade do ciclo para esta config, sem
  // esperar o cron. O back aplica a mesma regra de vencimento, então isto não
  // fura a carência nem duplica cobrança — quando não há ciclo novo a abrir,
  // ele responde `created: false` com o motivo.
  const handleGenerateNow = async (config: SubscriptionConfigInterface) => {
    setGeneratingId(config.config_id);
    try {
      const { data } = await api.post(
        `/subscription/config/${config.config_id}/generate`
      );

      if (data?.created === false) {
        autoInfo(data.message || "Nenhuma mensalidade nova a gerar.");
      } else {
        autoSuccess("Mensalidade gerada! Confira na tela de Mensalidades.");
      }
    } catch (error: any) {
      console.error("Erro ao gerar mensalidade:", error);
      handleHttpError(error);
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}
      >
        <Title>Assinaturas</Title>

        <CreateButton
          onClick={handleOpenCreate}
          style={{ marginLeft: "14px", marginBottom: "14px" }}
        >
          <FiCreditCard size={18} /> Criar Assinatura
        </CreateButton>
      </div>

      {isAdmin && (
        <ProductSearchCard>
          <TextField
            label="Data de Início"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toLocaleDateString("en-CA") }}
          />

          <TextField
            disabled={!startDate}
            label="Data Final"
            type="date"
            value={startDate ? endDate : ""}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: startDate || undefined,
              max: new Date().toLocaleDateString("en-CA")
            }}
          />

          <TextFeldUserSearch
            id="subscription_config_search"
            label="Buscar por empresa ou CNPJ"
            variant="outlined"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </ProductSearchCard>
      )}

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Empresa</th>
              <th>CNPJ</th>
              <th>Dia Venc.</th>
              <th>Valor</th>
              <th>Saldo Devedor</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {configs.map((config) => {
              const companyName = config.company?.company_name || "N/A";

              return (
                <tr key={config.config_id}>
                  {/* title guarda o nome inteiro, que a coluna corta em 20 */}
                  <td title={companyName}>{truncateText(companyName)}</td>
                  <td>{config.company?.company_cnpj || "N/A"}</td>
                  <td>{config.due_day}</td>
                  <td>R$ {parseFloat(`${config.amount}`).toFixed(2)}</td>
                  <td
                    style={{
                      color:
                        Number(config.outstanding_balance) > 0
                          ? "red"
                          : undefined,
                      fontWeight:
                        Number(config.outstanding_balance) > 0
                          ? "bold"
                          : "normal"
                    }}
                  >
                    R$ {parseFloat(`${config.outstanding_balance}`).toFixed(2)}
                  </td>

                  <td>
                    <Chip
                      label={TypeSubscriptionStatusLabel[config.status]}
                      size="small"
                      style={{
                        backgroundColor:
                          TypeSubscriptionStatusColor[config.status],
                        color: "#fff"
                      }}
                    />
                  </td>
                  {/* nowrap: os tres icones tem que caber numa linha so */}
                  <td style={{ whiteSpace: "nowrap" }}>
                    <CustomTooltip title="Gerar mensalidade agora">
                      <ActionButton
                        onClick={() => handleGenerateNow(config)}
                        disabled={generatingId === config.config_id}
                      >
                        <FiZap size={20} color="#3b82f6" />
                      </ActionButton>
                    </CustomTooltip>
                    <CustomTooltip title="Editar Configuração">
                      <ActionButton onClick={() => handleOpenEdit(config)}>
                        <FiEdit size={20} color="#004336" />
                      </ActionButton>
                    </CustomTooltip>
                    <CustomTooltip title="Excluir Assinatura">
                      <ActionButton onClick={() => setConfigToDelete(config)}>
                        <FiTrash2 size={20} color="#f44336" />
                      </ActionButton>
                    </CustomTooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      {/* Modal de Criação/Edição */}
      <SubscriptionCreateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubscriptionSaved={handleSubscriptionSaved}
        subscription={selectedSubscription}
      />

      <ConfirmDialog
        open={configToDelete !== null}
        title="Excluir Assinatura"
        description={
          configToDelete
            ? `Tem certeza que deseja excluir a assinatura de "${
                configToDelete.company?.company_name || "empresa"
              }"? As mensalidades dela também serão removidas. Esta ação não pode ser desfeita.`
            : "Tem certeza que deseja excluir esta assinatura?"
        }
        confirmText="Excluir"
        onClose={() => setConfigToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
};
